import { addDays, compareDates, type LocalDate } from '../dates';
import { dayDiff } from '../quit/dayMath';
import type { WeightEntryLike } from './types';

export const AVERAGE_DAYS = 7;

/** Live entries, oldest first. */
export function liveEntries(entries: readonly WeightEntryLike[]): WeightEntryLike[] {
  return entries.filter((e) => e.deletedAt === null).sort((a, b) => compareDates(a.date, b.date));
}

export function latestEntry(entries: readonly WeightEntryLike[]): WeightEntryLike | null {
  const live = liveEntries(entries);
  return live[live.length - 1] ?? null;
}

/**
 * The average of every weigh-in in the 7 days ending `date` (that day included), in whole grams.
 * Null when there is none, so a gap shows as a gap instead of a made-up number.
 */
export function averageEnding(entries: readonly WeightEntryLike[], date: LocalDate): number | null {
  const from = addDays(date, -(AVERAGE_DAYS - 1));
  const inWindow = entries.filter(
    (e) =>
      e.deletedAt === null && compareDates(e.date, from) >= 0 && compareDates(e.date, date) <= 0,
  );
  if (inWindow.length === 0) return null;
  return Math.round(inWindow.reduce((s, e) => s + e.valueG, 0) / inWindow.length);
}

export interface TrendPoint {
  date: LocalDate;
  /** That day's weigh-in, or null. */
  valueG: number | null;
  /** 7-day moving average ending that day, or null. */
  avgG: number | null;
}

/** One point per calendar day for the `days` days ending `today`. */
export function weightTrend(
  entries: readonly WeightEntryLike[],
  today: LocalDate,
  days: number,
): TrendPoint[] {
  const live = liveEntries(entries);
  const byDate = new Map(live.map((e) => [e.date, e.valueG]));
  const out: TrendPoint[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = addDays(today, -i);
    out.push({ date, valueG: byDate.get(date) ?? null, avgG: averageEnding(live, date) });
  }
  return out;
}

/**
 * Weekly rate of change in grams (negative = going down): the least-squares slope of the weigh-ins
 * in the last 28 days, times 7. Null until there are 3 weigh-ins spanning at least 7 days, so a
 * single heavy morning never reads as a trend.
 */
export function weeklyRate(entries: readonly WeightEntryLike[], today: LocalDate): number | null {
  const from = addDays(today, -27);
  const pts = liveEntries(entries).filter(
    (e) => compareDates(e.date, from) >= 0 && compareDates(e.date, today) <= 0,
  );
  if (pts.length < 3) return null;
  const xs = pts.map((e) => dayDiff(from, e.date));
  if (xs[xs.length - 1]! - xs[0]! < 7) return null;
  const n = pts.length;
  const mx = xs.reduce((s, x) => s + x, 0) / n;
  const my = pts.reduce((s, e) => s + e.valueG, 0) / n;
  let num = 0;
  let den = 0;
  pts.forEach((e, i) => {
    num += (xs[i]! - mx) * (e.valueG - my);
    den += (xs[i]! - mx) ** 2;
  });
  if (den === 0) return null;
  return Math.round((num / den) * 7);
}
